import { Helmet } from "react-helmet-async";

const SITE_NAME = "NEXORIQ";
const SITE_URL = "https://www.nexoriq.co";
const DEFAULT_OG = `${SITE_URL}/og-default.jpg`;

/**
 * Per-page SEO — title, meta description, canonical, OG + Twitter cards,
 * optional JSON-LD structured data.
 *
 * Props:
 *  - title           short page title (SITE_NAME suffix added automatically)
 *  - description     ~155-char meta description
 *  - path            canonical path, e.g. "/capabilities"
 *  - ogImage         absolute URL (falls back to default)
 *  - structuredData  optional array of JSON-LD objects
 *  - noindex         set true for pages we do NOT want indexed
 */
export default function Seo({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG,
  structuredData,
  noindex = false,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const canonical = `${SITE_URL}${path}`;
  const ld = structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : [];
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {/* Structured data */}
      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

// Shared JSON-LD: Organization (used on multiple pages)
export const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NEXORIQ FZ LLC",
  alternateName: "NEXORIQ",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    "NEXORIQ is a UAE-based global sourcing partner helping industrial companies source difficult-to-find, technically complex, urgent and specialized products through a qualified worldwide supplier network.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "VUNE2559 Compass Building, Al Hulaila Industrial Zone-FZ",
    postOfficeBoxNumber: "10055",
    postalCode: "10055",
    addressLocality: "Ras Al Khaimah",
    addressCountry: "AE",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "Sourcing",
      email: "source@nexoriq.co",
      telephone: "+971-50-800-3202",
      areaServed: "Worldwide",
      availableLanguage: ["en"],
    },
  ],
  sameAs: [],
};

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
};
